// "Your hospital record" panel on the public booking page -- the patient-side
// twin of HimsPatientLookup (admin). Same two ways in (automatic by mobile once
// the number is usable, or the hospital number typed in), same three outcomes
// kept visibly distinct: found / new here / could not reach the hospital
// system. Talks to the /public/hims/* routes, which sit behind the hospital's
// OTP setting, so the parent passes the booking token and only enables the
// panel once the phone is usable (verified, or the hospital has OTP off).
import React, { useCallback, useEffect, useState } from "react";
import {
  publicHimsLookup,
  publicHimsSearch,
  PublicHimsMatched,
  PublicHimsPatient,
  PublicHimsStatus,
} from "../../lib/apis/publicBooking";

interface Props {
  hospitalId: string | undefined;
  /** hims_patient_lookup flag. Off = render nothing. */
  enabled: boolean;
  /** Full mobile with country code, ONLY once it is complete and usable; "" = don't search. */
  mobile: string;
  bookingToken: string | null;
  onApply: (patient: PublicHimsPatient, matched: PublicHimsMatched | null) => void;
  /** Bumped by the parent when the applied record stops being valid. */
  resetToken?: number;
}

const PublicHimsLookup: React.FC<Props> = ({ hospitalId, enabled, mobile, bookingToken, onApply, resetToken = 0 }) => {
  const [mrn, setMrn] = useState("");
  const [status, setStatus] = useState<PublicHimsStatus | "idle" | "error">("idle");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<PublicHimsPatient[]>([]);
  const [matched, setMatched] = useState<PublicHimsMatched | null>(null);
  const [vendorName, setVendorName] = useState("the hospital's system");
  const [appliedMrn, setAppliedMrn] = useState<string | null>(null);

  const reset = () => {
    setStatus("idle");
    setResults([]);
    setMatched(null);
    setAppliedMrn(null);
  };

  const searchByMobile = useCallback(
    async (fullMobile: string) => {
      if (!hospitalId || !fullMobile) return;
      setLoading(true);
      try {
        const r = await publicHimsSearch(hospitalId, fullMobile, bookingToken);
        setVendorName(r.vendor_name || "the hospital's system");
        setResults(r.hims || []);
        setMatched(r.doctrue || null);
        setStatus(r.hims_status);
      } catch {
        setStatus("error");
      } finally {
        setLoading(false);
      }
    },
    [hospitalId, bookingToken]
  );

  useEffect(() => {
    reset();
    setMrn("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetToken]);

  useEffect(() => {
    if (!enabled || !hospitalId) return;
    if (!mobile) {
      reset();
      return;
    }
    if (appliedMrn) return;
    searchByMobile(mobile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, hospitalId, mobile, searchByMobile]);

  const lookupMrn = async () => {
    const key = mrn.trim();
    if (!hospitalId || !key || !mobile) return;
    setLoading(true);
    try {
      const r = await publicHimsLookup(hospitalId, mobile, key, bookingToken);
      setVendorName(r.vendor_name || "the hospital's system");
      if (!r.found || !r.patient) {
        setResults([]);
        setMatched(null);
        setStatus("not_found");
        return;
      }
      setResults([r.patient]);
      setMatched(r.linked || r.matched || null);
      setStatus("ok");
    } catch (e: any) {
      if (e?.response?.status === 503) {
        setResults([]);
        setMatched(null);
        setStatus("unreachable");
        setVendorName(e?.response?.data?.error?.vendor_name || "the hospital's system");
      } else {
        setStatus("error");
      }
    } finally {
      setLoading(false);
    }
  };

  const apply = (p: PublicHimsPatient) => {
    onApply(p, matched);
    setAppliedMrn(p.mrn);
  };

  if (!enabled) return null;

  // If this panel is off (hospital has no record source configured) say nothing
  // at all rather than an empty box.
  if (status === "off") return null;

  const describe = (p: PublicHimsPatient) =>
    [p.gender, p.age !== null && p.age !== undefined ? `${p.age} yrs` : null, p.guardian]
      .filter(Boolean)
      .join(" · ");

  const locked = !mobile;

  return (
    <div className="mt-4 md:mt-3.5 rounded-lg border border-doctorsBorder bg-[#FAFBFF] p-3">
      <div className="flex items-center justify-between">
        <p className="text-modalText text-[13px] md:text-sm font-semibold">Your hospital record</p>
        {loading && <span className="text-[11px] text-gray-400">Checking…</span>}
      </div>
      <p className="mt-0.5 text-[11px] text-queueText">
        {locked
          ? "Enter and verify your number to look up your record."
          : "We check the hospital's records for your number. You can also type your hospital number."}
      </p>

      <div className="mt-2 flex gap-2">
        <input
          className="flex-1 min-w-0 h-10 border border-doctorsBorder rounded-lg px-3 text-sm focus:outline-none focus:border-[#0F3CBE] disabled:bg-gray-50"
          placeholder="Hospital number (optional)"
          value={mrn}
          maxLength={64}
          disabled={locked}
          onChange={(e) => setMrn(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              lookupMrn();
            }
          }}
        />
        <button
          type="button"
          className="h-10 rounded-lg px-4 bg-[#0F3CBE] text-white text-sm font-semibold disabled:opacity-40"
          disabled={locked || loading || !mrn.trim()}
          onClick={lookupMrn}
        >
          Find
        </button>
      </div>

      {status === "unreachable" && (
        <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-2.5">
          <p className="text-[13px] font-semibold text-amber-800">Couldn't reach {vendorName}</p>
          <p className="text-[11px] text-amber-700 mt-0.5">You can still book; your hospital number just won't be attached.</p>
        </div>
      )}
      {status === "not_found" && (
        <p className="mt-2 text-[12px] text-gray-500">No record found for this number. You'll be registered as a new patient.</p>
      )}
      {status === "error" && (
        <p className="mt-2 text-[12px] text-red-600">Could not complete the lookup. Please try again.</p>
      )}
      {status === "ok" && results.length > 0 && (
        <div className="mt-2 flex flex-col gap-2 max-h-60 overflow-y-auto">
          {results.length > 1 && (
            <p className="text-[11px] text-gray-500">{results.length} records share this number. Pick the right person.</p>
          )}
          {results.map((p) => (
            <div
              key={p.mrn}
              className={`flex items-center justify-between gap-2 rounded-lg border p-2.5 ${
                appliedMrn === p.mrn ? "border-green-400 bg-green-50" : "border-doctorsBorder bg-white"
              }`}
            >
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-modalText truncate">{p.full_name || "(no name on record)"}</p>
                <p className="text-[11px] text-gray-500 truncate">{describe(p)}</p>
                <p className="text-[11px] text-gray-400">No: {p.mrn}</p>
              </div>
              <button
                type="button"
                className="shrink-0 h-9 rounded-lg px-3 text-[13px] font-semibold bg-[#0F3CBE] text-white disabled:opacity-40"
                disabled={appliedMrn === p.mrn}
                onClick={() => apply(p)}
              >
                {appliedMrn === p.mrn ? "Using" : "Use details"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PublicHimsLookup;
